import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "../ui/button";
import { Download, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders one SKU × Store matrix PER DAY, never summing across days.
 *
 * Props:
 *   rows: [{ created_at: ISO, store_name: string, items: [{sku, qty}] }]
 *   filenamePrefix: used for Excel download (e.g. "stock-matrix")
 *   testidPrefix: data-testid prefix (e.g. "client-stock" -> "client-stock-day-2026-06-01")
 *
 * Behaviour:
 *   - For each day, keep the LATEST entry per (date, store).
 *   - Build SKU × Store totals for that day (sum of items in the latest entry).
 *   - The newest day is expanded by default; older days are collapsed and show
 *     a one-line summary (outlets, SKUs, grand total). Click to expand/collapse.
 */
export default function DayWiseStockMatrices({
  rows,
  filenamePrefix = "stock-matrix",
  testidPrefix = "stock",
}) {
  const days = useMemo(() => {
    const byDay = new Map(); // date -> Map<storeName, items[]>
    const seenStorePerDay = new Map();
    for (const r of rows || []) {
      const date = (r.created_at || "").slice(0, 10);
      const storeName = r.store_name || "—";
      if (!byDay.has(date)) {
        byDay.set(date, new Map());
        seenStorePerDay.set(date, new Set());
      }
      const seen = seenStorePerDay.get(date);
      if (seen.has(storeName)) continue;
      seen.add(storeName);
      byDay.get(date).set(storeName, r.items || []);
    }

    const out = [];
    for (const [date, storeMap] of byDay) {
      const stores = Array.from(storeMap.keys()).sort();
      const perSku = {};
      for (const [storeName, items] of storeMap) {
        for (const it of items) {
          const sku = it.sku || "—";
          const qty = Number(it.qty || 0);
          if (!perSku[sku]) perSku[sku] = {};
          perSku[sku][storeName] = (perSku[sku][storeName] || 0) + qty;
        }
      }
      const skuRows = Object.entries(perSku)
        .map(([sku, byStore]) => {
          let total = 0;
          const obj = { sku };
          for (const st of stores) {
            const v = Number(byStore[st] || 0);
            obj[st] = v;
            total += v;
          }
          obj.total = total;
          return obj;
        })
        .sort((a, b) => a.sku.localeCompare(b.sku));
      const totals = { __grand__: 0 };
      for (const st of stores) {
        totals[st] = skuRows.reduce((acc, r) => acc + Number(r[st] || 0), 0);
        totals.__grand__ += totals[st];
      }
      out.push({ date, stores, rows: skuRows, totals });
    }
    out.sort((a, b) => (a.date < b.date ? 1 : -1));
    return out;
  }, [rows]);

  // Only the newest (first) day is expanded by default.
  const [expandedDates, setExpandedDates] = useState(() => new Set());
  const isExpanded = (date, idx) =>
    expandedDates.has(date) ||
    (idx === 0 && !expandedDates.has(`__collapsed__${date}`));
  const toggle = (date, idx) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      const defaultOpen = idx === 0 && !next.has(`__collapsed__${date}`);
      const open = next.has(date) || defaultOpen;
      if (open) {
        next.delete(date);
        if (idx === 0) next.add(`__collapsed__${date}`); // remember user collapsed it
      } else {
        next.add(date);
        next.delete(`__collapsed__${date}`);
      }
      return next;
    });
  };
  const expandAll = () => {
    setExpandedDates(new Set(days.map((d) => d.date)));
  };
  const collapseAll = () => {
    // Collapse everything including the default-open newest day.
    setExpandedDates(new Set(days.map((d, i) => (i === 0 ? `__collapsed__${d.date}` : null)).filter(Boolean)));
  };

  const downloadExcel = () => {
    if (!days.length) {
      toast.error("No stock data to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    for (const d of days) {
      const aoa = [];
      aoa.push(["SKU", ...d.stores, "Total"]);
      d.rows.forEach((r) => {
        aoa.push([r.sku, ...d.stores.map((st) => r[st] ?? 0), r.total ?? 0]);
      });
      aoa.push([
        "TOTAL",
        ...d.stores.map((st) => d.totals[st] ?? 0),
        d.totals.__grand__ ?? 0,
      ]);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [{ wch: 28 }, ...d.stores.map(() => ({ wch: 14 })), { wch: 12 }];
      const safe = d.date.replace(/[^0-9A-Za-z-]/g, "");
      XLSX.utils.book_append_sheet(wb, ws, safe.slice(0, 31));
    }
    XLSX.writeFile(wb, `${filenamePrefix}-by-day.xlsx`);
    toast.success("Excel downloaded");
  };

  if (!days.length) {
    return (
      <div className="glass-panel p-6 text-center text-neutral-500">
        No data
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-daywise`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          {days.length} day{days.length === 1 ? "" : "s"} · newest on top
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl h-8"
            onClick={expandAll}
            data-testid={`${testidPrefix}-daywise-expand-all`}
          >
            Expand all
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl h-8"
            onClick={collapseAll}
            data-testid={`${testidPrefix}-daywise-collapse-all`}
          >
            Collapse all
          </Button>
          <Button
            size="sm"
            className="rounded-xl bg-slate-900 text-white hover:bg-slate-700 h-8"
            onClick={downloadExcel}
            data-testid={`${testidPrefix}-daywise-export`}
          >
            <Download className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {days.map((d, idx) => {
        const open = isExpanded(d.date, idx);
        return (
          <div
            key={d.date}
            className="glass-panel"
            data-testid={`${testidPrefix}-day-${d.date}`}
          >
            <button
              type="button"
              onClick={() => toggle(d.date, idx)}
              className="w-full flex items-center justify-between bg-white/50 hover:bg-white/80 border-b border-slate-200/70 px-4 py-2 text-left"
              data-testid={`${testidPrefix}-day-${d.date}-toggle`}
            >
              <div className="flex items-center gap-2">
                {open ? (
                  <ChevronDown className="h-4 w-4 text-neutral-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-neutral-500" />
                )}
                <Calendar className="h-4 w-4 text-neutral-500" />
                <span className="font-mono text-[11px] uppercase tracking-widest">
                  {d.date}
                </span>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                {d.stores.length} outlet{d.stores.length === 1 ? "" : "s"} ·{" "}
                {d.rows.length} SKU{d.rows.length === 1 ? "" : "s"} · grand total{" "}
                <span className="text-black font-semibold">{d.totals.__grand__}</span>
              </div>
            </button>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white border-b border-slate-200/70">
                    <tr>
                      <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        SKU
                      </th>
                      {d.stores.map((st) => (
                        <th
                          key={st}
                          className="text-right p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500 whitespace-nowrap"
                        >
                          {st}
                        </th>
                      ))}
                      <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500 bg-amber-50">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r) => (
                      <tr key={r.sku} className="border-t border-slate-200/70">
                        <td className="p-3">{r.sku}</td>
                        {d.stores.map((st) => (
                          <td key={st} className="p-3 text-right font-mono">
                            {r[st] || 0}
                          </td>
                        ))}
                        <td className="p-3 text-right font-mono font-semibold bg-amber-50">
                          {r.total}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-black bg-white/50">
                      <td className="p-3 font-mono uppercase text-[10px] tracking-widest">
                        Total
                      </td>
                      {d.stores.map((st) => (
                        <td key={st} className="p-3 text-right font-mono font-semibold">
                          {d.totals[st] || 0}
                        </td>
                      ))}
                      <td className="p-3 text-right font-mono font-bold bg-amber-100">
                        {d.totals.__grand__}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
